<script setup lang="ts">
import { timeoutDelay } from '@/helpers/utils/timeoutDelay'
import LineItemReviewBlock from '@/components/projects/branding/line-item-review/LineItemReviewBlock.vue'
import { useUserStore } from '@/store/user'
import { onBeforeUnmount, onMounted } from 'vue'
import { onWaitingRoomAutoSyncRefresh } from '@/helpers/events/waitingRoomAutoSync'

interface IProps {
  modelValue: boolean,
  projectId?: string | number,
  workOrderId?: string | number,
  lineItemId?: string | number,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  projectId: '',
  workOrderId: '',
  lineItemId: '',
})
interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onApprovalAction'): void
  (e: 'uploaded'): void
}

const emit = defineEmits<Emit>()

const userStore = useUserStore()

const loading = ref<boolean>(false)

const hasApprovalAction = ref<boolean>(false)

const lineItemReviewBlockRef = ref<InstanceType<typeof LineItemReviewBlock> | null>()

const closeDialog = () => {
  lineItemReviewBlockRef.value?.closeFilesUploadDialog()
  emit('update:modelValue', false)
}

const onApprovalAction = () => {
  hasApprovalAction.value = true

  if (userStore.isLinkCreatedUser) {
    closeDialog()
  }
}

const onClose = async () => {
  if (hasApprovalAction.value) {
    emit('onApprovalAction')
    hasApprovalAction.value = false // Reset for next time
  }
  emit('onClose')
}

let unsubscribeWaitingRoomAutoSyncRefresh: (() => void) | null = null

onMounted(() => {
  unsubscribeWaitingRoomAutoSyncRefresh = onWaitingRoomAutoSyncRefresh(result => {
    const isCurrentLineItemAffected = result.affected_targets.some(target => (
      target.target_type === 'line_item' && String(target.target_id) === String(props.lineItemId)
    ))

    if (props.modelValue && isCurrentLineItemAffected) {
      void lineItemReviewBlockRef.value?.initReviewBlock()
    }
  })
})

onBeforeUnmount(() => {
  unsubscribeWaitingRoomAutoSyncRefresh?.()
})

watch(() => props.modelValue, async () => {
  // On show dialog
  if (props.modelValue) {
    await timeoutDelay(0)
    lineItemReviewBlockRef.value?.initReviewBlock()
  }
  // On hide dialog
  else {
    lineItemReviewBlockRef.value?.closeFilesUploadDialog()
    await onClose()
  }
})

</script>

<template>
  <div>
    <CustomVDialog
      :model-value="modelValue"
      fullscreen
      class="default-dialog line-item-review-dialog"
      @after-leave="onClose"
      @update:modelValue="emit('update:modelValue', $event)"
    >
      <VBtn
        variant="elevated"
        density="compact"
        icon="tabler-x"
        :disabled="!!loading"
        class="v-dialog-close-btn"
        @click="closeDialog"
      />

      <VCard>
        <template #title>
          <div class="line-item-review-title-row">
            <span>Line Item</span>
            <VBtn
              v-if="lineItemReviewBlockRef?.isFileUploadSupported"
              :disabled="!lineItemReviewBlockRef?.isFileUploadReady"
              class="ma-1"
              color="primary"
              variant="flat"
              size="small"
              @click="lineItemReviewBlockRef?.openFilesUploadDialog()"
            >
              <VIcon start icon="tabler-upload" />
              Upload Files
            </VBtn>
          </div>
        </template>
        <LineItemReviewBlock
          ref="lineItemReviewBlockRef"
          :project-id="projectId"
          :work-order-id="workOrderId"
          :line-item-id="lineItemId"
          :is-review-dialog="true"
          @onApprovalAction="onApprovalAction"
          @uploaded="emit('uploaded')"
        />
      </VCard>
    </CustomVDialog>
  </div>
</template>

<style lang="scss">
.line-item-review-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;

  > span {
    white-space: normal;
  }

  > .v-btn {
    flex-shrink: 0;
  }
}

</style>
